import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowRight, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import type { CurrentUser } from "../lib/types";

// Reuses the platform's existing e-mail OTP login — the console adds no second
// credential store. The superadmin check happens server-side on every request;
// this screen only decides where to send you afterwards.
export default function Login() {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const signIn = useAuthStore((s) => s.signIn);
  const navigate = useNavigate();

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/request_otp", { email });
      setStep("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Något gick fel");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await api.post<{ token: string; user: CurrentUser }>("/auth/verify_otp", { email, code });
      if (!data.user.is_superadmin) {
        setError("Det här kontot har inte behörighet till konsolen.");
        return;
      }
      signIn(data.token, data.user);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fel kod");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-7 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-lg border border-ink-700 bg-ink-900">
            <Shield className="h-5 w-5 text-accent" />
          </div>
          <h1 className="text-lg font-semibold text-white">Superadmin Console</h1>
          <p className="mt-1 text-sm text-ink-500">Endast för plattformsteamet</p>
        </div>

        <div className="panel p-5">
          {step === "email" ? (
            <form onSubmit={requestCode} className="space-y-3">
              <label className="block text-xs font-medium text-ink-400">E-post</label>
              <input
                type="email" required autoFocus value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="field" placeholder="du@minbesiktning.se"
              />
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center">
                {busy ? "Skickar…" : "Skicka inloggningskod"} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-3">
              <label className="block text-xs font-medium text-ink-400">
                Kod skickad till <span className="text-ink-200">{email}</span>
              </label>
              <input
                inputMode="numeric" required autoFocus value={code} maxLength={6}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="field text-center text-lg tracking-[0.4em]" placeholder="••••••"
              />
              <button type="submit" disabled={busy || code.length < 6} className="btn-primary w-full justify-center">
                {busy ? "Verifierar…" : "Logga in"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("email"); setCode(""); setError(null); }}
                className="w-full text-center text-xs text-ink-500 hover:text-ink-300"
              >
                Använd en annan e-post
              </button>
            </form>
          )}

          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-red-900/60 bg-red-950/40 p-2.5 text-xs text-red-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
